// Field/column configuration for CRM resources, shared by tables and forms.
export const STAGES = [
  ["identified", "Identified"], ["researching", "Researching"], ["qualified", "Qualified"], ["engaged", "Engaged"],
  ["proposal", "Proposal"], ["negotiation", "Negotiation"], ["won", "Won"], ["lost", "Lost"],
];
export const CLASSIFICATIONS = [
  ["further_research_required", "Further Research Required"], ["strategic_prospect", "Strategic Prospect"],
  ["active_project_signal", "Active Project Signal"], ["confirmed_procurement", "Confirmed Procurement"],
  ["rejected", "Rejected"],
];
export const COMPANY_STATUS = [["prospect", "Prospect"], ["customer", "Customer"], ["partner", "Partner"], ["competitor", "Competitor"], ["inactive", "Inactive"]];
export const SITE_TYPES = [["office", "Office"], ["warehouse", "Warehouse"], ["depot", "Depot"], ["distribution_centre", "Distribution centre"], ["factory", "Factory"], ["campus", "Campus"], ["car_park", "Car park"], ["port", "Port"], ["retail", "Retail"], ["hospital", "Hospital"], ["other", "Other"]];
export const SENIORITY = [["executive", "Executive"], ["director", "Director"], ["manager", "Manager"], ["specialist", "Specialist"], ["other", "Other"]];
export const CONSENT = [["unknown", "Unknown"], ["legitimate_interest", "Legitimate interest"], ["opted_in", "Opted in"], ["opted_out", "Opted out"]];
export const TASK_TYPES = [["follow_up", "Follow-up"], ["call", "Call"], ["research", "Research"], ["email", "Email"], ["meeting", "Meeting"], ["other", "Other"]];
export const PRIORITY = [["low", "Low"], ["medium", "Medium"], ["high", "High"]];
export const TASK_STATUS = [["open", "Open"], ["in_progress", "In progress"], ["done", "Done"], ["cancelled", "Cancelled"]];
export const ACTIVITY_TYPES = [["note", "Note"], ["call", "Call"], ["meeting", "Meeting"], ["email_logged", "Email (logged)"]];
export const SCORES = [
  ["commercial_fit", "Commercial Fit"], ["buying_signal_strength", "Buying Signal Strength"],
  ["research_confidence", "Research Confidence"], ["contact_readiness", "Contact Readiness"],
];

export const labelOf = (options, value) => options.find(([v]) => v === value)?.[1] || value || "—";

export const companies = {
  path: "companies", singular: "Company", testid: "companies", lookups: ["members"], bulkDelete: true,
  search: "Search name, domain, city, industry…",
  filters: [{ name: "status", label: "Status", options: COMPANY_STATUS }],
  columns: [
    { key: "name", label: "Company", strong: true },
    { key: "industry", label: "Industry" },
    { key: "city", label: "City" },
    { key: "country", label: "Country" },
    { key: "domain", label: "Domain", mono: true },
    { key: "status", label: "Status", options: COMPANY_STATUS, badge: true },
    { key: "updated_at", label: "Updated", date: true },
  ],
  fields: [
    { name: "name", label: "Company name", required: true, span: 2 },
    { name: "legal_name", label: "Legal name" }, { name: "registration_number", label: "Registration number" },
    { name: "domain", label: "Website domain", placeholder: "example.com" }, { name: "website", label: "Website URL" },
    { name: "industry", label: "Industry" }, { name: "status", label: "Status", type: "select", options: COMPANY_STATUS },
    { name: "country", label: "Country" }, { name: "region", label: "Region" },
    { name: "city", label: "City" }, { name: "postcode", label: "Postcode" },
    { name: "registered_address", label: "Registered address", span: 2 },
    { name: "employee_count", label: "Employees", type: "number" }, { name: "annual_revenue", label: "Annual revenue", type: "number" },
    { name: "owner_user_id", label: "Owner", type: "ref", ref: "members" },
    { name: "tags", label: "Tags (one per line)", type: "list" },
    { name: "description", label: "Description", type: "textarea", span: 2 },
  ],
};

export const sites = {
  path: "sites", singular: "Operating site", testid: "sites", lookups: ["companies"], search: "Search site, city, postcode…",
  filters: [{ name: "site_type", label: "Type", options: SITE_TYPES }],
  columns: [
    { key: "name", label: "Site", strong: true }, { key: "company_id", label: "Company", ref: "companies" },
    { key: "site_type", label: "Type", options: SITE_TYPES, badge: true }, { key: "city", label: "City" },
    { key: "postcode", label: "Postcode", mono: true }, { key: "country", label: "Country" },
  ],
  fields: [
    { name: "name", label: "Site name", required: true }, { name: "company_id", label: "Company", type: "ref", ref: "companies", required: true },
    { name: "site_type", label: "Site type", type: "select", options: SITE_TYPES, default: "other" }, { name: "address_line", label: "Address" },
    { name: "city", label: "City" }, { name: "region", label: "Region" }, { name: "postcode", label: "Postcode" }, { name: "country", label: "Country" },
    { name: "latitude", label: "Latitude", type: "number" }, { name: "longitude", label: "Longitude", type: "number" },
    { name: "notes", label: "Notes", type: "textarea", span: 2 },
  ],
};

export const contacts = {
  path: "contacts", singular: "Contact", testid: "contacts", lookups: ["companies", "members"],
  search: "Search name, email, job title…",
  filters: [{ name: "seniority", label: "Seniority", options: SENIORITY }, { name: "consent_status", label: "Consent", options: CONSENT }],
  columns: [
    { key: "last_name", label: "Name", strong: true, render: (r) => [r.first_name, r.last_name].filter(Boolean).join(" ") },
    { key: "job_title", label: "Job title" }, { key: "company_id", label: "Company", ref: "companies" },
    { key: "email", label: "Email", mono: true }, { key: "consent_status", label: "Consent", options: CONSENT, badge: true },
    { key: "updated_at", label: "Updated", date: true },
  ],
  fields: [
    { name: "first_name", label: "First name" }, { name: "last_name", label: "Last name", required: true },
    { name: "email", label: "Email" }, { name: "phone", label: "Phone" },
    { name: "job_title", label: "Job title" }, { name: "department", label: "Department" },
    { name: "seniority", label: "Seniority", type: "select", options: SENIORITY, nullable: true },
    { name: "company_id", label: "Company", type: "ref", ref: "companies" },
    { name: "consent_status", label: "Contact consent basis", type: "select", options: CONSENT },
    { name: "owner_user_id", label: "Owner", type: "ref", ref: "members" },
    { name: "linkedin_url", label: "Profile URL (manually entered)", span: 2 },
    { name: "notes", label: "Notes", type: "textarea", span: 2 },
  ],
};

export const opportunities = {
  path: "opportunities", singular: "Opportunity", testid: "opportunities", lookups: ["companies", "products", "contacts", "sites", "members"],
  search: "Search opportunity name or summary…",
  filters: [{ name: "stage", label: "Stage", options: STAGES }, { name: "classification", label: "Classification", options: CLASSIFICATIONS }],
  columns: [
    { key: "name", label: "Opportunity", strong: true }, { key: "company_id", label: "Company", ref: "companies" },
    { key: "stage", label: "Stage", options: STAGES, badge: true },
    { key: "classification", label: "Classification", options: CLASSIFICATIONS, badge: true },
    { key: "value", label: "Value", money: true, align: "right" },
    { key: "commercial_fit", label: "Fit", score: true }, { key: "research_confidence", label: "Confidence", score: true },
    { key: "updated_at", label: "Updated", date: true },
  ],
  fields: [
    { name: "name", label: "Opportunity name", required: true, span: 2 },
    { name: "company_id", label: "Company", type: "ref", ref: "companies", required: true },
    { name: "site_id", label: "Operating site", type: "ref", ref: "sites" },
    { name: "product_profile_id", label: "Product profile", type: "ref", ref: "products" },
    { name: "primary_contact_id", label: "Primary contact", type: "ref", ref: "contacts" },
    { name: "stage", label: "Stage", type: "select", options: STAGES },
    { name: "classification", label: "Commercial classification", type: "select", options: CLASSIFICATIONS },
    { name: "value", label: "Estimated value", type: "number" }, { name: "currency", label: "Currency", default: "GBP", required: true },
    { name: "expected_close_date", label: "Expected close", type: "date" }, { name: "owner_user_id", label: "Owner", type: "ref", ref: "members" },
    ...SCORES.map(([name, label]) => ({ name, label: `${label} (0–100)`, type: "number" })),
    { name: "summary", label: "Summary", type: "textarea", span: 2 }, { name: "next_step", label: "Next step", span: 2 },
  ],
};

export const tasks = {
  path: "tasks", singular: "Task", testid: "tasks", lookups: ["companies", "opportunities", "contacts", "members"],
  search: "Search tasks…",
  filters: [{ name: "status", label: "Status", options: TASK_STATUS }, { name: "priority", label: "Priority", options: PRIORITY }],
  columns: [
    { key: "title", label: "Task", strong: true }, { key: "type", label: "Type", options: TASK_TYPES },
    { key: "priority", label: "Priority", options: PRIORITY, badge: true }, { key: "status", label: "Status", options: TASK_STATUS, badge: true },
    { key: "due_date", label: "Due", date: true }, { key: "company_id", label: "Company", ref: "companies" },
    { key: "assignee_user_id", label: "Assignee", ref: "members" },
  ],
  fields: [
    { name: "title", label: "Title", required: true, span: 2 },
    { name: "type", label: "Type", type: "select", options: TASK_TYPES }, { name: "priority", label: "Priority", type: "select", options: PRIORITY, default: "medium" },
    { name: "status", label: "Status", type: "select", options: TASK_STATUS }, { name: "due_date", label: "Due date", type: "date" },
    { name: "assignee_user_id", label: "Assignee", type: "ref", ref: "members" }, { name: "company_id", label: "Company", type: "ref", ref: "companies" },
    { name: "opportunity_id", label: "Opportunity", type: "ref", ref: "opportunities" }, { name: "contact_id", label: "Contact", type: "ref", ref: "contacts" },
    { name: "description", label: "Description", type: "textarea", span: 2 },
  ],
};

const listField = (name, label) => ({ name, label, type: "list", span: 2 });
export const products = {
  path: "product-profiles", singular: "Product profile", testid: "products", search: "Search product profiles…",
  filters: [{ name: "status", label: "Status", options: [["active", "Active"], ["archived", "Archived"]] }],
  columns: [
    { key: "name", label: "Product", strong: true }, { key: "description", label: "Description", truncate: true },
    { key: "target_markets", label: "Target markets", render: (r) => (r.target_markets || []).join(", ") || "—" },
    { key: "status", label: "Status", badge: true, options: [["active", "Active"], ["archived", "Archived"]] },
    { key: "updated_at", label: "Updated", date: true },
  ],
  fields: [
    { name: "name", label: "Product name", required: true },
    { name: "status", label: "Status", type: "select", options: [["active", "Active"], ["archived", "Archived"]] },
    { name: "description", label: "Description", type: "textarea", span: 2 },
    listField("technical_capabilities", "Technical capabilities"), listField("target_markets", "Target markets"),
    listField("ideal_customer_characteristics", "Ideal customer characteristics"), listField("problems_solved", "Operational problems solved"),
    listField("positive_buying_signals", "Positive buying signals"), listField("exclusion_criteria", "Exclusion criteria"),
    listField("typical_decision_makers", "Typical decision-makers"), listField("competitors", "Competitors"),
    listField("case_studies", "Case studies (titles or links)"),
    { name: "qualification_instructions", label: "Qualification instructions", type: "textarea", span: 2 },
    { name: "search_terms", label: "Search terms (short buyer-language phrases, one per line — used to find tenders and web signals)", type: "list", span: 2 },
  ],
};
